import Head from "next/head";
import { useRouter } from "next/router";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import ReactHtmlParser from "react-html-parser";
import Image from "next/image";

import { ExtendedNextPage } from "types/pages";

import Label from "components/Label";
import Button from "components/Button";
import Post from "components/Post";
import HummingNav from "pages/components/HummingNav";
import HummingFooter from "pages/components/HummingFooter";
import { HowItWorks, YourMembership } from "pages/homepage/components";
import TheDifference from "pages/homepage/components/TheDifference";
import {
  getLocationServiceBySlug,
  getServices,
  Service,
  LocationService as ApiPost
} from "utils/content/services";
import Header from "../../components/Header";
import Mandala from "../../../../../public/404-mandala.png";

import styles from "./styles.module.scss";
import Content from "components/Content";

function formatLocation(inputString: string): string {
  // Split the input string by hyphen
  const parts: string[] = inputString.split("-");

  // Extract the last part (state code) and capitalize it
  const stateCode: string | undefined = parts.pop()?.toUpperCase();

  // Title case the remaining parts and join them with space
  const formattedString: string = parts
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  // Concatenate the formatted string with the state code
  const result = `${formattedString}, ${stateCode}`;

  return result;
}

function replaceHyphenWithSpace(inputString: string): string {
  if (inputString.includes("-")) {
    return inputString.replace(/-/g, " ");
  }
  return inputString;
}

const Page: ExtendedNextPage = () => {
  const router = useRouter();
  const {
    query: { locationSlug, serviceSlug }
  } = router;
  const [post, setPost] = useState<ApiPost>();
  const [loaded, setLoaded] = useState(false);
  const [services, setServices] = useState<Array<Service>>([]);

  async function fetchPost({
    location,
    service
  }: {
    location: string;
    service: string;
  }) {
    const data = await getLocationServiceBySlug({ location, service });
    if (data) {
      setPost(data);
    } else {
      setPost(undefined);
    }
    setLoaded(true);
  }

  async function fetchServices() {
    const data = await getServices();
    if (data) {
      setServices(data.items);
    }
  }

  useEffect(() => {
    if (locationSlug && serviceSlug) {
      fetchPost({
        location: String(locationSlug),
        service: String(serviceSlug)
      });
    }
  }, [locationSlug, serviceSlug]);

  useEffect(() => {
    fetchServices();
  }, []);

  const servicesToList = services
    .filter((service) => service.fields.slug !== serviceSlug)
    .sort((a, b) => {
      const aName = a.fields.title.toUpperCase();
      const bName = b.fields.title.toUpperCase();
      return aName > bName ? 1 : aName < bName ? -1 : 0;
    });

  const thumbnail = undefined;
  const cityState = post ? formatLocation(post.fields.locationSlug) : "";
  const title = post
    ? `The best ${post.fields.serviceSlug} company in ${cityState}`
    : "";

  return loaded ? (
    <div className={styles.container}>
      <Head>
        <title>{post && title} | Humming Homes</title>
        <meta name="description" content={post?.fields.metaDescription} />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      {post && (
        <Header
          title={title}
          subtitle={cityState}
          image={thumbnail ? `https:${thumbnail}` : undefined}
        />
      )}

      {/* Temporary until we fill out all services in prod: */}
      {!post && (
        <Content
          containerClassName={styles.contentWrapper404}
          contentClassName={styles.content404}
        >
          <Label
            className={styles.title404}
            color="primary"
            variant="h2-centered"
          >
            Coming soon
          </Label>
          <Label
            className={styles.subtitle404}
            color="primary"
            variant="s2-centered"
            useMarkdown
            linkColor="accent"
          >
            Click [here](/) to go back to our homepage
          </Label>
          <div className={styles.image404}>
            <Image layout="fill" objectFit="contain" alt="" src={Mandala} />
          </div>
        </Content>
      )}

      <div className={styles.postContainer}>
        {post && (
          <div className={styles.post}>
            <Post>{ReactHtmlParser(post?.fields.bodyHtml)}</Post>

            <div className={styles.ctaFooter}>
              <div className={styles.footerLeft}>
                <Label
                  variant="h2"
                  className={styles.footerHeadline}
                  renderNode
                >
                  When it comes to{" "}
                  {replaceHyphenWithSpace(post.fields.serviceSlug)}, Humming
                  Homes has you covered
                </Label>
                <Label variant="s" className={styles.footerBody}>
                  Join today to start having more good days at home
                </Label>
              </div>

              <div className={styles.footerRight}>
                <Button
                  onClick={() => {
                    router.push("/start");
                  }}
                >
                  Get Started
                </Button>
              </div>
            </div>
          </div>
        )}

        <aside className={styles.services}>
          <div className={styles.servicesInner}>
            <Label variant="s-capitalized" className={styles.servicesHeading}>
              {`More services in ${cityState}`}
            </Label>

            <ul className={styles.servicesList}>
              {servicesToList.map((service) => (
                <li key={service.fields.slug}>
                  <Link
                    href={`/services/${locationSlug}/${service.fields.slug}`}
                  >
                    <Label className={styles.service} variant="s" renderNode>
                      <img
                        src={`/services/icons/${service.fields.slug}.svg`}
                        alt={`${service.fields.title} icon`}
                      />
                      <span className={styles.otherServiceTitle}>
                        {service.fields.title}
                      </span>
                    </Label>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      <HowItWorks />
      <YourMembership />
      <TheDifference />
    </div>
  ) : null;
};

Page.getLayout = (page) => {
  return (
    <div>
      <HummingNav />
      {page}
      <HummingFooter />
    </div>
  );
};

export default Page;
